import type { TextDirection } from "@yjsgames/i18n";

/**
 * 좌상단 기준(origin x=0) 요소의 x 좌표를 방향에 맞게 미러링.
 * - LTR: x 그대로 (**항등** — ko/en 등 기존 LTR 화면 회귀 0)
 * - RTL: 컨테이너 폭 안에서 좌우 반전 → `totalWidth - x - elementWidth`
 *   (요소의 우측 가장자리가 좌측 가장자리로 가도록)
 */
export function mirrorX(
  x: number,
  elementWidth: number,
  totalWidth: number,
  dir: TextDirection,
): number {
  if (dir !== "rtl") return x;
  return totalWidth - x - elementWidth;
}

/** 중심점(origin x=0.5) 좌표를 미러링. RTL이면 `totalWidth - cx`, LTR이면 항등. */
export function mirrorCenterX(cx: number, totalWidth: number, dir: TextDirection): number {
  if (dir !== "rtl") return cx;
  return totalWidth - cx;
}

/** origin x(0~1)를 미러링. RTL이면 `1 - originX`(좌측 정렬 ↔ 우측 정렬), LTR이면 항등. */
export function mirrorAnchorX(originX: number, dir: TextDirection): number {
  if (dir !== "rtl") return originX;
  return 1 - originX;
}

/** 가로 배치 1개 아이템 — 폭만 알면 된다. */
export interface RowLayoutItem {
  readonly width: number;
}

export interface RowLayoutResult {
  /** 각 아이템의 좌측 x 좌표 (origin x=0 기준), 입력 순서 그대로. */
  readonly xs: number[];
  /** 전체 행 폭 (아이템 폭 합 + 사이 gap). */
  readonly totalWidth: number;
}

/**
 * 자식 아이템을 가로로 배치할 때 각 아이템의 좌측 x 좌표를 계산.
 * - LTR: 좌→우 누적 — `[0, w0+gap, w0+gap+w1+gap, ...]`
 * - RTL: 우→좌 — 전체 폭에서 거울 대칭(논리상 첫 아이템이 우측 끝에 옴)
 *
 * gap은 아이템 **사이**에만 적용(앞뒤 패딩 없음). origin x=0 기준 좌표를 반환하므로
 * 호출자는 자식 origin을 (0, *)로 두고 `setX(xs[i])` 하면 된다.
 */
export function computeRowLayout(
  items: ReadonlyArray<RowLayoutItem>,
  gap: number,
  dir: TextDirection,
): RowLayoutResult {
  const totalWidth =
    items.reduce((sum, it) => sum + it.width, 0) + Math.max(0, items.length - 1) * gap;
  const xs: number[] = [];
  let cursor = 0;
  for (const it of items) {
    xs.push(dir === "rtl" ? totalWidth - cursor - it.width : cursor);
    cursor += it.width + gap;
  }
  return { xs, totalWidth };
}
